/**
 * How the Notification Center orders its list (#1093): filter by category
 * chip and the "only new" switch, bundle rows that share a `group_key`, then
 * split the result into day sections (today, yesterday, this week, older).
 *
 * Pure functions on the notifications the hook already holds — nothing here
 * talks to the server, so the panel and its tests share one source of truth.
 */
import type { Notification, NotificationCategory } from '@/services/notification-service'
import { notificationCategory, notificationGroupKey } from './notificationPayload'

/** Order of the category chips; a chip only shows when the list has a row of it. */
export const CATEGORY_ORDER: readonly NotificationCategory[] = ['bug_reports', 'documents', 'routines', 'requests', 'shared']

export type CategoryFilter = NotificationCategory | 'all'

export interface NotificationFilter {
  category: CategoryFilter
  onlyUnread: boolean
}

/** One row of the list: a single notification or a bundle of several with one `group_key`. */
export interface NotificationEntry {
  key: string
  /** Newest first. */
  items: Notification[]
  latest: Notification
  unreadCount: number
}

export type DayBucket = 'today' | 'yesterday' | 'thisWeek' | 'older'

export interface DaySection {
  bucket: DayBucket
  entries: NotificationEntry[]
}

export interface CategoryChip {
  category: NotificationCategory
  unread: number
}

const DAY_ORDER: readonly DayBucket[] = ['today', 'yesterday', 'thisWeek', 'older']

function time(notification: Notification): number {
  const value = new Date(notification.created_at).getTime()
  return Number.isNaN(value) ? 0 : value
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

/** Monday 00:00 of the week `date` falls in (the week starts on Monday in both UI languages' regions). */
function startOfWeek(date: Date): Date {
  const day = startOfDay(date)
  const sinceMonday = (day.getDay() + 6) % 7
  day.setDate(day.getDate() - sinceMonday)
  return day
}

/** The day section a timestamp belongs to, in the browser's local time. */
export function dayBucket(createdAt: string, now: Date = new Date()): DayBucket {
  const created = new Date(createdAt)
  if (Number.isNaN(created.getTime())) return 'older'
  const today = startOfDay(now)
  if (created >= today) return 'today'
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  if (created >= yesterday) return 'yesterday'
  if (created >= startOfWeek(now)) return 'thisWeek'
  return 'older'
}

export function filterNotifications(notifications: Notification[], filter: NotificationFilter): Notification[] {
  return notifications.filter((notification) => {
    if (filter.onlyUnread && notification.is_read) return false
    if (filter.category === 'all') return true
    return notificationCategory(notification) === filter.category
  })
}

/**
 * Rows sharing a `group_key` collapse into one entry, placed where its newest
 * row would be. Rows without a key stay single.
 */
export function bundleNotifications(notifications: Notification[]): NotificationEntry[] {
  const sorted = [...notifications].sort((a, b) => time(b) - time(a))
  const entries: NotificationEntry[] = []
  const byKey = new Map<string, NotificationEntry>()
  for (const notification of sorted) {
    const groupKey = notificationGroupKey(notification)
    const existing = groupKey ? byKey.get(groupKey) : undefined
    if (existing) {
      existing.items.push(notification)
      if (!notification.is_read) existing.unreadCount += 1
      continue
    }
    const entry: NotificationEntry = {
      key: groupKey ? `group:${groupKey}` : notification.id,
      items: [notification],
      latest: notification,
      unreadCount: notification.is_read ? 0 : 1,
    }
    entries.push(entry)
    if (groupKey) byKey.set(groupKey, entry)
  }
  return entries
}

/** Entries into day sections by their newest row; empty sections are left out. */
export function groupByDay(entries: NotificationEntry[], now: Date = new Date()): DaySection[] {
  const buckets = new Map<DayBucket, NotificationEntry[]>()
  for (const entry of entries) {
    const bucket = dayBucket(entry.latest.created_at, now)
    const list = buckets.get(bucket) ?? []
    list.push(entry)
    buckets.set(bucket, list)
  }
  return DAY_ORDER.filter((bucket) => buckets.has(bucket)).map((bucket) => ({ bucket, entries: buckets.get(bucket)! }))
}

export function buildSections(notifications: Notification[], filter: NotificationFilter, now: Date = new Date()): DaySection[] {
  return groupByDay(bundleNotifications(filterNotifications(notifications, filter)), now)
}

/** The chips to offer: every category present in the list, with its unread count. */
export function categoryChips(notifications: Notification[]): CategoryChip[] {
  const counts = new Map<NotificationCategory, number>()
  for (const notification of notifications) {
    const category = notificationCategory(notification)
    if (!category) continue
    counts.set(category, (counts.get(category) ?? 0) + (notification.is_read ? 0 : 1))
  }
  return CATEGORY_ORDER.filter((category) => counts.has(category)).map((category) => ({
    category,
    unread: counts.get(category)!,
  }))
}

/** Up to two initials of a display name ("Anna Keller" → "AK"). */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''
  const first = parts[0][0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : ''
  return (first + last).toUpperCase()
}
